import { useRef, useState } from "react"
import SearchIcon from '@mui/icons-material/Search';
import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment, LinearProgress, Radio, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField } from "@mui/material"
import { useQuery } from "react-query";
import useDebounce from "../../../lib/useDebounce";
import axiosClient from "../../../lib/axios";
import LoadingApiQuery from "../../LoadingApiQuery";
import { TExtraParams } from ".";
import { TEstabelecimento } from "../../../types/api/financeiro/estabelecimentos";
import CloseIcon from '@mui/icons-material/Close';

interface DialogSelectEstabelecimentosProps<M extends boolean = false> {
    open: boolean,
    handleClose: () => void,
    title: string,
    defValue: M extends true ? TEstabelecimento[] : TEstabelecimento | null,
    onSubmitValue: M extends true ? (arg: TEstabelecimento[]) => void : (arg: TEstabelecimento | null) => void,
    extraParams?: TExtraParams,
    multiple?: M
}

export default function DialogSelectEstabelecimentos<M extends boolean = false>({
    open,
    handleClose,
    title,
    defValue,
    onSubmitValue,
    extraParams,
    multiple = false as M
}: DialogSelectEstabelecimentosProps<M>) {

    const [keyword, setKeyword] = useState<string>("")
    const [selected, setSelected] = useState<TEstabelecimento[]>(
        multiple
            ? (defValue as TEstabelecimento[] || [])
            : (defValue ? [defValue as TEstabelecimento] : [])
    );
    const [limit, setLimit] = useState<number>(50)
    const tableContainerRef = useRef<HTMLDivElement>(null);

    //queries e mutations
    const debouncedKeyword = useDebounce(keyword)
    const { data, isFetching, isLoading } = useQuery({
        queryKey: ['/financeiro/integracao-bancaria/pesquisar-estabelecimentos', debouncedKeyword, limit],
        queryFn: () => axiosClient.get("/financeiro/integracao-bancaria/pesquisar-estabelecimentos", {
            params: {
                page: 1,
                limit: limit,
                pesquisar: debouncedKeyword,
            }
        }),
        retry: false,
        keepPreviousData: true,
        cacheTime: 0
    });
    const dadosEstabelecimentos: TEstabelecimento[] = data?.data.data || []
    //  

    //funções manipuladoras
    const handleSubmit = () => {
        if (selected) {
            if (multiple) {
                (onSubmitValue as (arg: TEstabelecimento[]) => void)(selected);
            } else {
                (onSubmitValue as (arg: TEstabelecimento | null) => void)(selected[0]);
            }
            handleClose();
        }
    }

    const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
        const bottom = event.currentTarget.scrollHeight - event.currentTarget.scrollTop === event.currentTarget.clientHeight;
        if (bottom && !isFetching) {
            setLimit((prevLimit) => prevLimit + 20)
        }
    };

    const handleLimpar = () => {
        setSelected([]);
        if (multiple) {
            (onSubmitValue as (arg: TEstabelecimento[]) => void)([]);
        } else {
            (onSubmitValue as (arg: TEstabelecimento | null) => void)(null);
        }
        handleClose();
    }

    const handleSelecionar = (row: TEstabelecimento) => {
        if (multiple) {
            if (selected.find((item) => item.id === row.id)) {
                setSelected(selected.filter((item) => item.id !== row.id))
            } else {
                setSelected([...selected, row])
            }
        } else {
            setSelected([row])
        }
    }
    //  

    //valores importantes
    const bloquearConfirmar = !selected
    //

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                scroll="body"
                fullWidth
                maxWidth="lg"
            >
                <DialogTitle >
                    Selecionar {title}
                </DialogTitle>
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={(theme) => ({
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: theme.palette.grey[500],
                    })}
                >
                    <CloseIcon />
                </IconButton>
                <DialogContent>
                    <Box py={2}>
                        <TextField
                            fullWidth
                            value={keyword}
                            label="Pesquisar"
                            onChange={({ target }) => setKeyword(target.value)}
                            slotProps={{
                                input: {
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <SearchIcon />
                                        </InputAdornment>
                                    )
                                }
                            }}
                        />

                        {isLoading && <LoadingApiQuery extraParams={{ height: 400, my: 1, alignItems: "center" }} />}

                        <Box mt={1}>{(isFetching && data) && <LinearProgress />}</Box>
                        {
                            (!isLoading && data) &&
                            <TableContainer
                                ref={tableContainerRef}
                                onScroll={handleScroll}
                                sx={{ height: 400, overflow: "auto", mb: 1 }}
                            >

                                <Table size="small" aria-label="a dense table" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell></TableCell>
                                            <TableCell><b>Loja</b></TableCell>
                                            <TableCell><b>Número estabelecimento</b></TableCell>
                                            <TableCell><b>Operadora</b></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {dadosEstabelecimentos.map((row) => {
                                            const identificador = row.id.toString()
                                            const checked = selected?.find((item) => item?.id === row.id)

                                            return (
                                                <TableRow
                                                    key={row.id}
                                                    sx={{
                                                        cursor: "pointer",
                                                        backgroundColor: checked ? "#edf7ed" : "unset",
                                                        '&:last-child td, &:last-child th': { border: 0 }
                                                    }}
                                                    onClick={() => {
                                                        handleSelecionar(row)
                                                    }}
                                                >
                                                    <TableCell>
                                                        {
                                                            !multiple &&
                                                            <Radio
                                                                name="radio-buttons"
                                                                checked={Boolean(checked)}
                                                                slotProps={{ input: { 'aria-label': identificador } }}
                                                            />
                                                        }
                                                        {
                                                            multiple &&
                                                            <Checkbox
                                                                name="radio-buttons"
                                                                checked={Boolean(checked)}
                                                                slotProps={{ input: { 'aria-label': identificador } }}
                                                            />
                                                        }
                                                    </TableCell>
                                                    <TableCell>{row.loja_id} - {row.loja_nome}</TableCell>
                                                    <TableCell>
                                                        {row.numero_estabelecimento}
                                                    </TableCell>
                                                    <TableCell>{row.operadora_nome}</TableCell>
                                                </TableRow>
                                            )
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        }
                    </Box>
                </DialogContent>

                <DialogActions>
                    <Button
                        onClick={handleLimpar}
                    >
                        Limpar
                    </Button>
                    <Button
                        autoFocus
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={bloquearConfirmar}
                    >
                        Selecionar
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

